/* eslint-disable react-hooks/exhaustive-deps */
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import React from 'react';
import Button from '@/components/molecules/button/button';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { MainNavigationMenuItem } from '../../main-navigation';
import MainNavigationLargeDropdown from '../main-navigation-large-dropdown/main-navigation-large-dropdown';
import MainNavigationLargeMenuItem from '../main-navigation-large-menu-item/main-navigation-large-menu-item';
import styles from './main-navigation-large-menu-item-list.module.scss';

export interface MainNavigationLargeMenuItemListProps {
  /** The additional menu items to render */
  children?: React.ReactNode;
  /** The class name to apply */
  className?: string;
  /** Indicates whether this is a featured item list */
  isFeatured?: boolean;
  /** Indicates whether the dropdown is open */
  isOpen?: boolean;
  /** Indicates whether this is a priority item list */
  isPriority?: boolean;
  /** Indicates whether this is a quick menu item */
  isQuickMenu?: boolean;
  /** Indicates whether the component is on a transparent navigation */
  isTransparent?: boolean;
  /** An array of links to show */
  items?: MainNavigationMenuItem[];
  /** An event that is triggered on close */
  onClose?: () => void;
  /** An event that is triggered on open */
  onOpen?: () => void;
}

const SCROLL_OFFSET = 480;

const MainNavigationLargeMenuItemList = ({
  children,
  className,
  isFeatured = false,
  isOpen = false,
  isPriority = false,
  isQuickMenu = false,
  isTransparent = false,
  items = [],
  onClose,
  onOpen,
}: MainNavigationLargeMenuItemListProps) => {
  const [currentDropdown, setCurrentDropdown] =
    useState<MainNavigationMenuItem>();
  const componentRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
  const [hasOverflowRight, setHasOverflowRight] = useState(false);
  const [scrollX, setScrollX] = useState(0);
  const { t } = useI18n();

  useEffect(() => {
    if (isPriority) return;

    const handleResize = () => {
      if (!componentRef.current) return;

      if (!itemsRef.current) return;

      const componentWidth = componentRef.current.offsetWidth;
      const itemsWidth = itemsRef.current.offsetWidth;

      setHasOverflowRight(itemsWidth > componentWidth);
      setHasOverflowLeft(false);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [items]);

  useEffect(() => {
    if (isOpen) return;

    setCurrentDropdown(undefined);
  }, [isOpen]);

  const handleClick = (
    item: MainNavigationMenuItem,
    event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (item.type === 'default' && (!item.items || item.items.length === 0)) {
      setCurrentDropdown(undefined);
      onClose && onClose();

      return;
    }

    event?.preventDefault();

    if (currentDropdown === item) {
      setCurrentDropdown(undefined);
      onClose && onClose();
    } else {
      setCurrentDropdown(item);
      onOpen && onOpen();
    }
  };

  const handleClickPrevious = () => {
    if (!componentRef.current) return;

    if (!itemsRef.current) return;

    const wrapperWidth = componentRef.current.getBoundingClientRect().width;

    const elementWidth = itemsRef.current.getBoundingClientRect().width;

    const overflowLeft = scrollX + SCROLL_OFFSET <= 0;
    const overflowRight =
      scrollX + SCROLL_OFFSET >= wrapperWidth - elementWidth;

    setScrollX(!overflowLeft ? 0 : scrollX + SCROLL_OFFSET);

    setHasOverflowLeft(overflowLeft);
    setHasOverflowRight(overflowRight);
  };

  const handleClickNext = () => {
    if (!componentRef.current) return;

    if (!itemsRef.current) return;

    const wrapperWidth = componentRef.current.getBoundingClientRect().width;

    const elementWidth = itemsRef.current.getBoundingClientRect().width;

    const overflowLeft = scrollX - SCROLL_OFFSET <= 0;
    const overflowRight =
      scrollX - SCROLL_OFFSET >= wrapperWidth - elementWidth;

    setScrollX(
      !overflowRight ? wrapperWidth - elementWidth : scrollX - SCROLL_OFFSET,
    );

    setHasOverflowLeft(overflowLeft);
    setHasOverflowRight(overflowRight);
  };

  const handleKeyup = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setCurrentDropdown(undefined);
      onClose && onClose();
    }
  };

  useEffect(() => {
    window.addEventListener('keyup', handleKeyup);

    return () => {
      window.removeEventListener('keyup', handleKeyup);
    };
  }, []);

  return (
    <div
      ref={componentRef}
      className={classNames(styles.component, className, {
        [styles.hasOverflowLeft]: hasOverflowLeft,
        [styles.hasOverflowRight]: hasOverflowRight,
        [styles.isTransparent]: isTransparent,
      })}
    >
      {hasOverflowLeft && (
        <Button
          aria-label={t('items.common.words.previous')}
          className={classNames(styles.navigationButton, styles.isPrevious)}
          icon="ChevronLeft"
          onClick={handleClickPrevious}
          variant="tertiary"
        />
      )}

      <div className={styles.itemsWrapper}>
        <motion.div
          ref={itemsRef}
          animate={{ left: scrollX }}
          className={styles.items}
          transition={{ ease: 'easeInOut', type: 'tween' }}
        >
          {items &&
            items.map((item, itemIndex) => {
              const hasDropdownItems = (item.items?.length || 0) > 0;
              const isDropdownTrigger =
                item.type === 'search' || hasDropdownItems;

              return (
                <React.Fragment key={itemIndex}>
                  <MainNavigationLargeMenuItem
                    {...item}
                    isActive={isOpen && item === currentDropdown}
                    isDropdownTrigger={isDropdownTrigger}
                    isFeatured={isFeatured || item.isFeatured}
                    isPriority={isPriority || item.isPriority}
                    isQuickMenu={isQuickMenu}
                    isTransparent={isTransparent}
                    onClick={
                      isDropdownTrigger
                        ? event => event && handleClick(item, event)
                        : undefined
                    }
                    type={item.type}
                  />

                  {isDropdownTrigger && (
                    <MainNavigationLargeDropdown
                      isOpen={isOpen && item === currentDropdown}
                      item={item}
                      onClick={
                        item.type !== 'search'
                          ? () => {
                              setCurrentDropdown(undefined);
                              onClose && onClose();
                            }
                          : undefined
                      }
                      onClose={() => {
                        setCurrentDropdown(undefined);
                        onClose && onClose();
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}

          {children && children}
        </motion.div>
      </div>

      {hasOverflowRight && (
        <Button
          aria-label={t('items.common.words.next')}
          className={classNames(styles.navigationButton, styles.isNext)}
          icon="ChevronRight"
          onClick={handleClickNext}
          variant="tertiary"
        />
      )}
    </div>
  );
};

MainNavigationLargeMenuItemList.displayName = 'MainNavigationLargeMenuItemList';

export default MainNavigationLargeMenuItemList;
